import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import * as uiActions from '~actions/ui-actions';

import * as identityProviderAPI from '~api/identity-providers';
import * as spacesAPI from '~api/spaces';

import * as dialogs from '~components/dialogs/dialog-types';
import ModalDialog from '~components/dialogs/modal-dialog';
import ActionButton from '~components/general/action-button';
import IString from '~components/general/i-string';
import { IdentityProvider } from '~components/identity-providers/identity-provider-prop-types';
import classnames from '~components/util/classnames';

import { translate } from '~i18n/localize';

const CLOUD = 'cloud';
const CONNECT_CLOUD = 'connect_cloud';

const initialState = {
  product: CLOUD,
  connectCloudAccountName: '',
  cloudAccountId: 0,
  cloudSpaceId: 0,
  generatedURL: '',
  errors: {
    connectCloudAccountName: false,
    cloudAccountId: false,
    cloudSpaceId: false
  }
};

export default function IdentityProviderURLDialog({ data }) {
  const { identityProvider, linkedAccounts } = data;

  const [ product, setProduct ] = useState(initialState.product);
  const [ connectCloudAccountName, setConnectCloudAccountName ] = useState(initialState.connectCloudAccountName);
  const [ cloudAccountId, setCloudAccountId ] = useState(initialState.cloudAccountId);
  const [ cloudSpaceId, setCloudSpaceId ] = useState(initialState.cloudSpaceId);
  const [ generatedURL, setGeneratedURL ] = useState(initialState.generatedURL);
  const [ cloudSpaceOptions, setCloudSpaceOptions ] = useState([]);
  const [ errors, setErrors ] = useState(initialState.errors);

  useEffect(() => {
    setGeneratedURL('');
  }, [ product ]);

  const cloudAccountOptions = useMemo(() => {
    const options = linkedAccounts
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
      .map(n => <option value={n.id} key={`account-${n.id}`}>{n.display_name || n.name} (ID: {n.id})</option>);
    options.unshift(
      <option value={initialState.cloudAccountId} key={-1}>
        {translate('identityProvider.dialog.generateURL.placeholders.cloudAccountName')}
      </option>
    );
    return options;
  }, [ linkedAccounts ]);

  useEffect(() => {
    if (cloudAccountId) {
      (async () => {
        try {
          const response = await spacesAPI.listSpaces({ account_id: `${cloudAccountId}` });
          const options = response.data.spaces
            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
            .map(n => <option value={n.id} key={`space-${n.id}`}>{n.name} (ID: {n.id})</option>);
          options.unshift(<option value={initialState.cloudSpaceId} key={-1}>{translate('identityProvider.dialog.generateURL.placeholders.cloudSpaceName')}</option>);
          setCloudSpaceOptions(options);
        } catch (error) { /* ignored */
        }
      })();
    } else {
      setCloudSpaceOptions([]);
    }
  }, [ cloudAccountId ]);

  const dispatch = useDispatch();

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedURL);
      dispatch(uiActions.setSuccessMessage(translate('identityProvider.dialog.generateURL.copied')));
    } catch (error) {
      dispatch(uiActions.setErrorMessage(error));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cloudAccountIdRequired = product === CLOUD && cloudAccountId === initialState.cloudAccountId;
    const cloudSpaceIdRequired = product === CLOUD && cloudSpaceId === initialState.cloudSpaceId;
    const connectCloudAccountNameRequired = product === CONNECT_CLOUD && connectCloudAccountName === initialState.connectCloudAccountName;

    if (cloudAccountIdRequired
        || cloudSpaceIdRequired
        || connectCloudAccountNameRequired) {
      setErrors({
        cloudAccountId: cloudAccountIdRequired,
        cloudSpaceId: cloudSpaceIdRequired,
        connectCloudAccountName: connectCloudAccountNameRequired
      });
      return;
    }
    setErrors(initialState.errors);
    
    const redirect = product === CLOUD ? `/spaces/${cloudSpaceId}/content` : `/${connectCloudAccountName}`;

    try {
      const response = await identityProviderAPI.generateIdentityProviderURL(identityProvider.id, { product, redirect });
      setGeneratedURL(response.data.url);
    } catch (error) {
      dispatch(uiActions.setErrorMessage(error));
    }
  };

  const handleChangeProduct = (e) => {
    const value = e.target.value;
    setProduct(value);
    // Reset everything
    setCloudAccountId(initialState.cloudAccountId);
    setCloudSpaceId(initialState.cloudSpaceId);
    setConnectCloudAccountName(initialState.connectCloudAccountName);
    setErrors(initialState.errors);
  };

  const handleChangeCloudAccountId = (e) => {
    const value = parseInt(e.target.value);
    setCloudAccountId(value);
    if (value === initialState.cloudAccountId) {
      setCloudSpaceId(initialState.cloudSpaceId);
        
    }
    if (value && errors.cloudAccountId) {
      setErrors({
        ...errors,
        cloudAccountId: false
      });
    }
  };

  const handleChangeCloudSpaceId = (e) => {
    const value = parseInt(e.target.value);
    setCloudSpaceId(value);
    if (value && errors.cloudSpaceId) {
      setErrors({
        ...errors,
        cloudSpaceId: false
      });
    }
  };

  const handleChangeConnectCloudAccountName = (e) => {
    const value = e.target.value.trim().replace(/\//g, '');
    setConnectCloudAccountName(value);
    if (value && errors.connectCloudAccountName) {
      setErrors({
        ...errors,
        connectCloudAccountName: false
      });
    }
  };

  return (
    <ModalDialog type={dialogs.IDENTITY_PROVIDER_DIALOG}>
      <div className="purpose">
        <IString
          stringKey="identityProvider.dialog.generateURL.title"
          trustPlaceholders
          placeholders={{ name: identityProvider.name, id: identityProvider.id }}
        />
      </div>
      <form noValidate onSubmit={handleSubmit}>
        <div className="controlGroup">
          <fieldset className="chunk">
            <legend>
              <IString stringKey="identityProvider.dialog.generateURL.product" />
            </legend>
            <div className="smallMarginBelow">
              <input
                checked={product === CLOUD}
                id="cloudProduct"
                name="product"
                onChange={handleChangeProduct}
                required
                type="radio"
                value={CLOUD}
              />
              <label htmlFor="cloudProduct">
                <IString stringKey="identityProvider.dialog.generateURL.cloud" />
              </label>
            </div>
            <div>
              <input
                checked={product === CONNECT_CLOUD}
                id="connectCloudProduct"
                name="product"
                onChange={handleChangeProduct}
                required
                type="radio"
                value={CONNECT_CLOUD}
              />
              <label className="midMarginRight" htmlFor="connectCloudProduct">
                <IString stringKey="identityProvider.dialog.generateURL.connectCloud" />
              </label>
            </div>
          </fieldset>
        </div>

        { product === CLOUD && <>
          <fieldset className="chunk">
            <label htmlFor="cloudAccountId" className={classnames('fieldLabel', { error: errors.cloudAccountId })}>
              <IString stringKey={'identityProvider.dialog.generateURL.cloudAccountId'} />
              { errors.cloudAccountId
                ? <IString stringKey={'identityProvider.dialog.generateURL.required'} className="errorText" />
                : null
              }
            </label>
            <div className="flex">
              <select
                className="full"
                id="cloudAccountId"
                onChange={handleChangeCloudAccountId}
                required
                value={cloudAccountId}
              >
                {cloudAccountOptions}
              </select>
            </div>
          </fieldset>
          <fieldset className="chunk">
            <label htmlFor="cloudSpaceId" className={classnames('fieldLabel', { error: errors.cloudSpaceId })}>
              <IString stringKey={'identityProvider.dialog.generateURL.cloudSpaceId'} />
              { errors.cloudSpaceId
                ? <IString stringKey={'identityProvider.dialog.generateURL.required'} className="errorText" />
                : null
              }
            </label>
            <div className="flex">
              <select
                className="full"
                id="cloudSpaceId"
                onChange={handleChangeCloudSpaceId}
                required
                value={cloudSpaceId}
                disabled={cloudAccountId === initialState.cloudAccountId}
              >
                {cloudSpaceOptions}
              </select>
            </div>
          </fieldset>
        </>}

        { product === CONNECT_CLOUD &&
          <fieldset className="chunk">
            <label htmlFor="connectCloudAccountName" className={classnames('fieldLabel', { error: errors.connectCloudAccountName })}>
              <IString stringKey={'identityProvider.dialog.generateURL.connectCloudAccountName'} />
              { errors.connectCloudAccountName
                ? <IString stringKey={'identityProvider.dialog.generateURL.required'} className="errorText" />
                : null
              }
            </label>
            <div className="flex">
              <input
                className="fullWidth"
                id="connectCloudAccountName"
                onChange={handleChangeConnectCloudAccountName}
                required
                type="text"
                value={connectCloudAccountName}
                placeholder={translate('identityProvider.dialog.generateURL.placeholders.connectCloudAccountName')}
              />
              <div className="actionBar inline" style={{ paddingTop: '3px' }}>
                <ActionButton className="delete noTitle" onClick={() => setConnectCloudAccountName(initialState.connectCloudAccountName)} />
              </div>
            </div>
          </fieldset>
        }

        <fieldset className="chunk">
          <div className="flex flexAlignBaseline">
            <label className="fieldLabel">
              <IString stringKey="identityProvider.dialog.generateURL.generated" />
            </label>
            <div className="actionBar inline showTitles" style={{ paddingBottom: '5px', paddingTop: '5px' }}>
              <ActionButton
                className="copy"
                title={translate('identityProvider.dialog.generateURL.copy')}
                disabled={!generatedURL}
                onClick={handleCopyToClipboard}
              />
            </div>
          </div>
          <pre style={{ height: '65px' }}>{generatedURL}</pre>
        </fieldset>

        <div className="actions">
          <button type="submit" disabled={Object.values(errors).includes(true)}><IString stringKey="identityProvider.dialog.generateURL.generate" /></button>
        </div>

      </form>
    </ModalDialog>
  );

}

IdentityProviderURLDialog.propTypes = {
  data: PropTypes.shape({
    identityProvider: IdentityProvider.isRequired,
    linkedAccounts: PropTypes.arrayOf(
      PropTypes.shape({
        display_name: PropTypes.string,
        id: PropTypes.number.isRequired,
        name: PropTypes.string
      })
    ).isRequired
  }),
  onSubmit: PropTypes.func
};
